import { useQuery } from '@tanstack/react-query';
import { useApi } from '../utils/useApi';
import { useUserContext } from '../contexts/UserContext';
import { 
  Users, 
  UserCheck, 
  UserMinus, 
  Clock, 
  AlertCircle, 
  TrendingUp, 
  Zap, 
  User,
  GripVertical
} from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip as RechartsTooltip } from 'recharts';

interface DashboardStats {
  totalMembers: number;
  statusBreakdown: {
    active: number;
    inactive: number;
    lead: number;
    trial: number;
  };
  overduePayments: {
    count: number;
    totalAmount: number;
  };
  genderDistribution: {
    male: { active: number; inactive: number };
    female: { active: number; inactive: number };
    other: { active: number; inactive: number };
  };
  checkinsToday: number;
  revenueBreakdown: {
    plans: number;
    products: number;
    services: number;
    other: number;
  };
}

const StatCard = ({ title, value, icon, subtitle, trend, trendColor = 'text-emerald-500' }: any) => (
  <div className="ai-glass-card rounded-[2rem] p-6 group transition-all duration-300 hover:scale-[1.02]">
    <div className="flex items-center justify-between mb-4">
      <div className="p-3 bg-indigo-50 rounded-2xl group-hover:bg-indigo-100 transition-colors">
        {icon}
      </div>
      {trend && (
        <span className={`text-xs font-black px-2 py-1 rounded-lg bg-gray-50 border border-gray-100 ${trendColor}`}>
          {trend}
        </span>
      )}
    </div>
    <div>
      <p className="text-[10px] font-black text-zinc-400 uppercase tracking-[0.2em] mb-1">{title}</p>
      <h3 className="text-3xl font-black text-zinc-900 tracking-tight">{value}</h3>
      {subtitle && <p className="text-xs font-medium text-zinc-500 mt-1">{subtitle}</p>}
    </div>
  </div>
);

export default function DashboardPage() {
  const { fetchWithAuth } = useApi();
  const { userContext, loading: userLoading } = useUserContext();

  const { data: stats, isLoading } = useQuery(['main-dashboard-stats', userContext?.user?.id], async () => {
    const response = await fetchWithAuth('/api/dashboard/main-stats');
    return response.data as DashboardStats;
  }, {
    enabled: !!userContext && !userLoading,
    staleTime: 5 * 60 * 1000
  });

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  const genderData = [
    { name: 'Male', value: (stats?.genderDistribution.male.active || 0) + (stats?.genderDistribution.male.inactive || 0), color: '#6366f1' },
    { name: 'Female', value: (stats?.genderDistribution.female.active || 0) + (stats?.genderDistribution.female.inactive || 0), color: '#ec4899' },
    { name: 'Not indicated', value: (stats?.genderDistribution.other.active || 0) + (stats?.genderDistribution.other.inactive || 0), color: '#94a3b8' }
  ].filter(d => d.value > 0);

  const revenueBreakdownData = [
    { name: 'Plans', value: stats?.revenueBreakdown.plans || 0, color: '#6366f1' },
    { name: 'Products', value: stats?.revenueBreakdown.products || 0, color: '#f43f5e' },
    { name: 'Services', value: stats?.revenueBreakdown.services || 0, color: '#10b981' },
    { name: 'Other', value: stats?.revenueBreakdown.other || 0, color: '#94a3b8' }
  ].filter(d => d.value > 0);

  return (
    <div className="space-y-8 pb-12 animate-in fade-in duration-700">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <h1 className="text-4xl font-black tracking-tighter">
            <span className="ai-gradient-text">DASHBOARD</span>
          </h1>
          <p className="text-zinc-500 font-bold uppercase tracking-widest text-[10px] mt-2">
            Centralized Command Center & Operations
          </p>
        </div>
      </div>

      {/* Primary Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard 
          title="Total Community" 
          value={stats?.totalMembers || 0} 
          icon={<Users className="w-6 h-6 text-indigo-600" />}
          subtitle="All-time registered members"
        />
        <StatCard 
          title="Active Members" 
          value={stats?.statusBreakdown.active || 0} 
          icon={<UserCheck className="w-6 h-6 text-emerald-600" />}
          subtitle="Currently following a plan"
          trend={`${stats?.statusBreakdown.active && stats?.totalMembers ? Math.round((stats.statusBreakdown.active / stats.totalMembers) * 100) : 0}%`}
          trendColor="text-emerald-600"
        />
        <StatCard 
          title="Overdue Dues" 
          value={`₹${stats?.overduePayments.totalAmount.toLocaleString() || 0}`} 
          icon={<AlertCircle className="w-6 h-6 text-rose-600" />}
          subtitle={`${stats?.overduePayments.count || 0} pending payments`}
          trend="Overdue"
          trendColor="text-rose-600"
        />
        <StatCard 
          title="Today's Checkins" 
          value={stats?.checkinsToday || 0} 
          icon={<Clock className="w-6 h-6 text-amber-600" />}
          subtitle="Real-time footfall today"
          trend="Live"
          trendColor="text-amber-600"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        {/* Status Breakdown - Deep Insights */}
        <div className="lg:col-span-8">
          <div className="ai-glass-card rounded-[2.5rem] p-8 h-full">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h3 className="text-xl font-black text-zinc-900 tracking-tight">Members count by status</h3>
                <p className="text-xs font-bold text-zinc-400 uppercase tracking-widest mt-1">Lifecycle breakdown</p>
              </div>
              <div className="p-3 bg-zinc-50 rounded-2xl">
                <GripVertical className="w-5 h-5 text-zinc-300" />
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div className="p-6 bg-emerald-50 rounded-3xl border border-emerald-100 flex flex-col items-center text-center group cursor-pointer hover:bg-emerald-100 transition-all">
                <div className="p-2 bg-white rounded-xl mb-3 shadow-sm group-hover:scale-110 transition-transform">
                  <UserCheck className="w-5 h-5 text-emerald-600" />
                </div>
                <p className="text-[10px] font-black text-emerald-600 uppercase tracking-widest mb-1">Active</p>
                <p className="text-2xl font-black text-emerald-900">{stats?.statusBreakdown.active || 0}</p>
              </div>

              <div className="p-6 bg-amber-50 rounded-3xl border border-amber-100 flex flex-col items-center text-center group cursor-pointer hover:bg-amber-100 transition-all">
                <div className="p-2 bg-white rounded-xl mb-3 shadow-sm group-hover:scale-110 transition-transform">
                  <TrendingUp className="w-5 h-5 text-amber-600" />
                </div>
                <p className="text-[10px] font-black text-amber-600 uppercase tracking-widest mb-1">Leads</p>
                <p className="text-2xl font-black text-amber-900">{stats?.statusBreakdown.lead || 0}</p>
              </div>

              <div className="p-6 bg-indigo-50 rounded-3xl border border-indigo-100 flex flex-col items-center text-center group cursor-pointer hover:bg-indigo-100 transition-all">
                <div className="p-2 bg-white rounded-xl mb-3 shadow-sm group-hover:scale-110 transition-transform">
                  <Zap className="w-5 h-5 text-indigo-600" />
                </div>
                <p className="text-[10px] font-black text-indigo-600 uppercase tracking-widest mb-1">Trial</p>
                <p className="text-2xl font-black text-indigo-900">{stats?.statusBreakdown.trial || 0}</p>
              </div>

              <div className="p-6 bg-slate-50 rounded-3xl border border-slate-100 flex flex-col items-center text-center group cursor-pointer hover:bg-slate-100 transition-all">
                <div className="p-2 bg-white rounded-xl mb-3 shadow-sm group-hover:scale-110 transition-transform">
                  <UserMinus className="w-5 h-5 text-slate-500" />
                </div>
                <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest mb-1">Inactive</p>
                <p className="text-2xl font-black text-slate-900">{stats?.statusBreakdown.inactive || 0}</p>
              </div>
            </div>

            {/* Visual Bar Chart (Custom representation) */}
            <div className="mt-12 space-y-4">
              <div className="flex items-center justify-between">
                <p className="text-[10px] font-black text-zinc-400 uppercase tracking-widest">Active vs Inactive Ratio</p>
                <p className="text-xs font-black text-zinc-900">{Math.round(((stats?.statusBreakdown.active || 0) / (stats?.totalMembers || 1)) * 100)}% Conversion</p>
              </div>
              <div className="h-4 bg-gray-100 rounded-full overflow-hidden flex shadow-inner">
                <div 
                  className="h-full bg-emerald-500 transition-all duration-1000" 
                  style={{ width: `${((stats?.statusBreakdown.active || 0) / (stats?.totalMembers || 1)) * 100}%` }}
                />
                <div 
                  className="h-full bg-amber-400 transition-all duration-1000" 
                  style={{ width: `${((stats?.statusBreakdown.lead || 0) / (stats?.totalMembers || 1)) * 100}%` }}
                />
                <div 
                  className="h-full bg-indigo-500 transition-all duration-1000" 
                  style={{ width: `${((stats?.statusBreakdown.trial || 0) / (stats?.totalMembers || 1)) * 100}%` }}
                />
              </div>
              <div className="flex flex-wrap gap-4 pt-2">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-emerald-500" />
                  <span className="text-[10px] font-bold text-zinc-500 uppercase">Active</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-amber-400" />
                  <span className="text-[10px] font-bold text-zinc-500 uppercase">Lead</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-indigo-500" />
                  <span className="text-[10px] font-bold text-zinc-500 uppercase">Trial</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-gray-200" />
                  <span className="text-[10px] font-bold text-zinc-500 uppercase">Inactive</span>
                </div>
              </div>
            </div>

            {/* Income Streams (Proper Categorization) */}
            <div className="mt-16 pt-10 border-t border-zinc-100">
               <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-6 mb-8">
                <div>
                  <h3 className="text-xl font-black text-zinc-900 tracking-tight">Income Streams</h3>
                  <p className="text-xs font-bold text-zinc-400 uppercase tracking-widest mt-1">Cross-category revenue split</p>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
                <div className="h-48 relative">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={revenueBreakdownData}
                        innerRadius={60}
                        outerRadius={80}
                        paddingAngle={5}
                        dataKey="value"
                      >
                        {revenueBreakdownData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} stroke="none" />
                        ))}
                      </Pie>
                      <RechartsTooltip 
                        content={({ active, payload }) => {
                          if (active && payload && payload.length) {
                            return (
                              <div className="bg-zinc-950/90 backdrop-blur-md border border-zinc-800 p-3 rounded-xl shadow-2xl">
                                <p className="text-[10px] font-black text-zinc-400 uppercase tracking-widest mb-1">{payload[0].name}</p>
                                <p className="text-sm font-bold text-white">{formatCurrency(payload[0].value as number)}</p>
                              </div>
                            );
                          }
                          return null;
                        }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                    <p className="text-xs font-black text-zinc-400 uppercase tracking-widest">Gross</p>
                    <p className="text-xl font-black text-zinc-900 leading-none mt-1">
                      {formatCurrency((stats?.revenueBreakdown.plans || 0) + (stats?.revenueBreakdown.products || 0) + (stats?.revenueBreakdown.services || 0) + (stats?.revenueBreakdown.other || 0))}
                    </p>
                  </div>
                </div>
                <div className="space-y-4">
                  <div className="flex items-center justify-between group">
                    <div className="flex items-center gap-2">
                       <div className="w-3 h-3 rounded-full bg-[#6366f1]" />
                       <span className="text-sm font-bold text-zinc-600 group-hover:text-zinc-900 transition-colors">Plans</span>
                    </div>
                    <span className="text-sm font-black text-zinc-900">{formatCurrency(stats?.revenueBreakdown.plans || 0)}</span>
                  </div>
                  <div className="flex items-center justify-between group">
                    <div className="flex items-center gap-2">
                       <div className="w-3 h-3 rounded-full bg-[#f43f5e]" />
                       <span className="text-sm font-bold text-zinc-600 group-hover:text-zinc-900 transition-colors">Products</span>
                    </div>
                    <span className="text-sm font-black text-zinc-900">{formatCurrency(stats?.revenueBreakdown.products || 0)}</span>
                  </div>
                  <div className="flex items-center justify-between group">
                    <div className="flex items-center gap-2">
                       <div className="w-3 h-3 rounded-full bg-[#10b981]" />
                       <span className="text-sm font-bold text-zinc-600 group-hover:text-zinc-900 transition-colors">Services</span>
                    </div>
                    <span className="text-sm font-black text-zinc-900">{formatCurrency(stats?.revenueBreakdown.services || 0)}</span>
                  </div>
                  <div className="flex items-center justify-between group">
                    <div className="flex items-center gap-2">
                       <div className="w-3 h-3 rounded-full bg-[#94a3b8]" />
                       <span className="text-sm font-bold text-zinc-600 group-hover:text-zinc-900 transition-colors">Other</span>
                    </div>
                    <span className="text-sm font-black text-zinc-900">{formatCurrency(stats?.revenueBreakdown.other || 0)}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Gender Distribution */}
        <div className="lg:col-span-4">
          <div className="ai-glass-card rounded-[2.5rem] p-8 h-full flex flex-col">
            <div className="mb-8">
              <h3 className="text-xl font-black text-zinc-900 tracking-tight">Gender Diversity</h3>
              <p className="text-xs font-bold text-zinc-400 uppercase tracking-widest mt-1">Cross-sectional analysis</p>
            </div>

            <div className="h-48 relative mb-8">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={genderData}
                    innerRadius={60}
                    outerRadius={80}
                    paddingAngle={5}
                    dataKey="value"
                  >
                    {genderData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} stroke="none" />
                    ))}
                  </Pie>
                  <RechartsTooltip />
                </PieChart>
              </ResponsiveContainer>
              <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                <p className="text-2xl font-black text-zinc-900 leading-none">{stats?.totalMembers || 0}</p>
                <p className="text-[8px] font-black text-zinc-400 uppercase mt-1">Total</p>
              </div>
            </div>

            <div className="space-y-4 mt-auto">
              {/* Male breakdown */}
              <div className="p-4 bg-zinc-50 rounded-2xl border border-zinc-100">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-xl bg-indigo-100 flex items-center justify-center">
                      <User className="w-4 h-4 text-indigo-600" />
                    </div>
                    <span className="text-xs font-black text-zinc-800 uppercase">Male</span>
                  </div>
                  <span className="text-xs font-black text-zinc-900">{(stats?.genderDistribution.male.active || 0) + (stats?.genderDistribution.male.inactive || 0)}</span>
                </div>
                <div className="flex items-center gap-6">
                  <div className="flex-1">
                    <p className="text-[8px] font-black text-zinc-400 uppercase mb-1">Active</p>
                    <p className="text-lg font-black text-indigo-600 leading-tight">{stats?.genderDistribution.male.active || 0}</p>
                  </div>
                  <div className="flex-1 border-l border-zinc-100 pl-4">
                    <p className="text-[8px] font-black text-zinc-400 uppercase mb-1">Inactive</p>
                    <p className="text-lg font-black text-indigo-300 leading-tight">{stats?.genderDistribution.male.inactive || 0}</p>
                  </div>
                </div>
              </div>

              {/* Female breakdown */}
              <div className="p-4 bg-zinc-50 rounded-2xl border border-zinc-100">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-xl bg-pink-100 flex items-center justify-center">
                      <User className="w-4 h-4 text-pink-600" />
                    </div>
                    <span className="text-xs font-black text-zinc-800 uppercase">Female</span>
                  </div>
                  <span className="text-xs font-black text-zinc-900">{(stats?.genderDistribution.female.active || 0) + (stats?.genderDistribution.female.inactive || 0)}</span>
                </div>
                <div className="flex items-center gap-6">
                  <div className="flex-1">
                    <p className="text-[8px] font-black text-zinc-400 uppercase mb-1">Active</p>
                    <p className="text-lg font-black text-pink-600 leading-tight">{stats?.genderDistribution.female.active || 0}</p>
                  </div>
                  <div className="flex-1 border-l border-zinc-100 pl-4">
                    <p className="text-[8px] font-black text-zinc-400 uppercase mb-1">Inactive</p>
                    <p className="text-lg font-black text-pink-300 leading-tight">{stats?.genderDistribution.female.inactive || 0}</p>
                  </div>
                </div>
              </div>

              {/* Other breakdown */}
              {((stats?.genderDistribution.other.active || 0) + (stats?.genderDistribution.other.inactive || 0)) > 0 && (
                <div className="p-4 bg-zinc-50 rounded-2xl border border-zinc-100">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-xl bg-slate-100 flex items-center justify-center">
                        <User className="w-4 h-4 text-slate-600" />
                      </div>
                      <span className="text-xs font-black text-zinc-800 uppercase">Not indicated</span>
                    </div>
                    <span className="text-xs font-black text-zinc-900">{(stats?.genderDistribution.other.active || 0) + (stats?.genderDistribution.other.inactive || 0)}</span>
                  </div>
                  <div className="flex items-center gap-6">
                    <div className="flex-1">
                      <p className="text-[8px] font-black text-zinc-400 uppercase mb-1">Active</p>
                      <p className="text-lg font-black text-slate-600 leading-tight">{stats?.genderDistribution.other.active || 0}</p>
                    </div>
                    <div className="flex-1 border-l border-zinc-100 pl-4">
                      <p className="text-[8px] font-black text-zinc-400 uppercase mb-1">Inactive</p>
                      <p className="text-lg font-black text-slate-300 leading-tight">{stats?.genderDistribution.other.inactive || 0}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
